import { computed, h } from 'vue';
import type { RouteMeta } from 'vue-router';
import { customAlphabet } from 'nanoid';
import { Message, Modal } from '@arco-design/web-vue';

import BigNumber from 'bignumber.js';
import dayjs, { OpUnitType } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import Fraction from 'fraction.js';
import { cloneDeep, uniq, isNumber, isUndefined, isFunction } from 'lodash';
import { storeToRefs } from 'pinia';

import { STATUS } from '@/components/uploader/props';

import { getMaterialPerUseNumber } from '@/api/product';
import { safeGetGoodsOrDesign } from '@/api/goods';
import globalContants from '@/constants/globalContants';
import areaJson from '@/constants/json/area.json';
import { KEYBOARD_KEY } from '@/constants/keyboard';
import orderState, { StateInfo } from '@/constants/order';
import store, { useSupplierStore } from '@/store';
import useUserStore from '@/store/modules/user/index';
import { factorySysConfigMap } from '@/views/factory/common';
import { queryUserConfigList, saveUserConfig } from '@/api/user';
import { FLOW_BUSINESS_KEY } from '@/views/flow/constants';
import defaultRestDays from '@/constants/json/restDays.json';

interface AreaItem {
  areaName: string;
  level: number;
  pid: number;
  id: number;
  children?: AreaItem[];
}
/**
 * 将扁平的地区数据转换为具有层级结构的数据
 * @param data - 扁平的地区数据数组
 * @returns 具有层级结构的地区数据
 */
function buildAreaTree(data: AreaItem[]): AreaItem[] {
  // 创建一个映射表，用于快速查找节点
  const nodeMap = new Map<number, AreaItem>();
  const rootNodes: AreaItem[] = [];

  // 初始化所有节点，添加children属性
  data.forEach((item) => {
    nodeMap.set(item.id, { ...item, children: [] });
  });

  // 构建树形结构
  data.forEach((item) => {
    const currentNode = nodeMap.get(item.id);
    if (item.level === 1) {
      // 顶级节点
      if (currentNode) {
        rootNodes.push(currentNode);
      }
    } else {
      // 子节点，找到父节点并添加到其children中
      const parentNode = nodeMap.get(item.pid);
      if (parentNode && currentNode) {
        parentNode.children?.push(currentNode);
      }
    }
  });

  return rootNodes;
}

// console.log(JSON.stringify(buildAreaTree(areaJson)));

dayjs.extend(relativeTime); // 相对时间 提供fromNow

const order: Recordable = {
  // STOCKFLOW: 'stockFlowState', // 库存流水列表单据状态
  OUTBOUNDORDER: 'outboundOrderState', // 出库单列表单据状态
  GOODSREWORKORDER: 'goodsReworkOrderState', // 出库单列表单据状态
  MATERIALINVENTORY: 'materialInventoryState', // 物料盘点单列表单据状态
  MATERIALTRANSFER: 'materialTransferState', // 物料调拨单列表单据状态
  MATERIALPICKING: 'materialPickingState', // 物料领料单列表单据状态
  MATERIALRETURN: 'materialReturnState', // 物料退料单列表单据状态
  FINISHTRANSFER: 'finishedTransferState', // 成品调拨单列表单据状态
  BORROWORDERSATET: 'borrowOrderState', // 借还单列表单据状态
  BACKORDERSATET: 'backOrderState', // 借还单列表单据状态
  BOMISSUESTATE: 'bomIssueState', // 生产单-bom的发料状态
  DESIGNPROSTATE: 'designProState', // 设计款-旗舰版状态
};

const userStore = useUserStore(store);
const supplierStore = useSupplierStore();
const {
  materialDecimal,
  hasProduceBatch,
  restDaysList,
  sysConfigMap,
  isAllowGoodsEdit,
} = storeToRefs(userStore);
// 除法
export function accDiv(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.div(arg2).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.div(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 乘法
export function accMul(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.multipliedBy(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.multipliedBy(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 加法
export function accAdd(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.plus(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.plus(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 减法
export function accSub(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.minus(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.minus(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 取余
export function accMod(arg1: any, arg2: any, decimal?: any) {
  try {
    if (typeof decimal === 'number') {
      const BN = BigNumber.clone({ DECIMAL_PLACES: decimal });
      const bn1 = new BN(arg1);
      return bn1.mod(arg2).div(1).toNumber();
    }
    const bn1 = BigNumber(arg1);
    return bn1.mod(arg2).toNumber();
  } catch (e) {
    return undefined;
  }
}

// 工具函数：日期转时间戳
export const toTime = (dateStr: string) => {
  return new Date(dateStr).getTime();
};

/**
 * 获取时间范围的unix对象
 * @param {Array} timeRange
 */
export function getUnixFromStr(timeRange: [string, string]) {
  return {
    begin: timeRange && timeRange.length === 2 ? dayjs(timeRange[0]).unix() : 0,
    end: timeRange && timeRange.length === 2 ? dayjs(timeRange[1]).unix() : 0,
  };
}
// 获取最近30天的日子，用于日期范围默认值
export function getMonthRange() {
  return [
    dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD'),
  ];
}
/**
 * 获取某个月的开始时间截止到今天
 * @param t
 */
export const someMonth = (t = new Date(), mode: OpUnitType = 'month') => {
  const beginDateTime = dayjs(t).startOf(mode).valueOf();
  const endDateTime = dayjs().endOf('day').valueOf();

  return [beginDateTime, endDateTime];
};
/**
 * 往前几个月
 */
export const prevMonth = (t: number) => {
  const startTime = dayjs().subtract(t, 'month').startOf('month').valueOf();
  const endTime = dayjs().endOf('day').valueOf();
  return [startTime, endTime];
};

/**
 * @description: 获取n月前指定日期
 * @param {*} n
 * @return {*} Unix Time Stamp e.g: 1652054400000
 */
export const getMonthAgoDate = (n = 0) => {
  const curDate = new Date();
  curDate.setMonth(curDate.getMonth() - n);
  return new Date(curDate).setHours(0, 0, 0, 0);
};

/**
 * 获取间隔num天
 * @param num 间隔天数：从今天开始往前推，默认1天则今天到今天
 * @return Array  返回开始和结束时间的数组
 */
export const getRecentDays = (num: number, format?: string) => {
  const start = getNewDate();
  const end = getNewDate();
  start.setDate(start.getDate() - num + 1);
  if (format) {
    return [dayjs(start).format(format), dayjs(end).format(format)];
  }
  return [start, end];
};

// 获取两个日期的间隔天数
export const getDateGapDays = (date: any[]) => {
  const [date1, date2] = date;
  return dayjs(date2).diff(dayjs(date1), 'day') + 1;
};
/**
 * 获取日期的年月日零点的Date，用于替换new Date原生的toLocaleDateString方法，解决toLocaleDateString在安卓显示bug
 * @param {Date} date
 * @param {String} format
 * @returns Date
 */
export const getNewDate = (date = new Date(), format = 'YYYY-MM-DD') => {
  // 相当于new Date(new Date().toLocaleDateString())
  return new Date(dayjs(date).format(format));
};
// 获取当天日期，用于日期选择器默认值
export function getToday() {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * 获取某天的开始时间，即0点
 * @param {Date} date
 */
export function getBeginningTime(
  date: string | number | Date = new Date()
): number {
  return new Date(date).setHours(0, 0, 0, 0);
}
export function getHoursTime(
  date: string | number | Date,
  hours: number,
  min?: number,
  sec?: number,
  ms?: number
): number {
  if (!date) date = new Date();
  return new Date(date).setHours(hours, min, sec, ms);
}

/**
 * 获取某天的结束时间，即23:59:59点
 * @param {Date} date
 */
export function getEndingTime(
  date: string | number | Date = new Date(),
  // 精度
  precision: 'sec' | 'ms' = 'ms'
): number {
  const overTimeObj = {
    hours: 23,
    min: 59,
    sec: 59,
    ms: 999,
  };
  if (precision === 'sec') {
    overTimeObj.ms = 0;
  }
  return new Date(date).setHours(
    overTimeObj.hours,
    overTimeObj.min,
    overTimeObj.sec,
    overTimeObj.ms
  );
}

export function getDiffDays(start: number, end: number) {
  const diffMs = end - start;
  const remainingMs = diffMs % (1000 * 60 * 60 * 24);
  const halfDayPrecision = remainingMs >= 1000 * 60 * 60 * 12 ? 0.5 : 0; // 如果超过半天则加0.5，否则不加
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  // 返回相差的天数，包括半天的精度
  return diffDays + halfDayPrecision;
}
// 结合以上两个，传时间范围数组
export function rangeTime(arr: (string | number | Date)[]): Array<number> {
  if (arr?.length < 2) {
    return [];
  }
  const [start, end]: (string | number | Date)[] = arr;
  const a = [getBeginningTime(start), getEndingTime(end)];
  return a;
}

// 获取默认时间范围数组（最近30天）往前推29+今天
export function getDefaultRangeTime(num = 30): Array<number> {
  const a = [getBeginningTime(getBeforeNumDay(num - 1)), getEndingTime()];
  return a;
}

// 获取相对于当前时间的前几天
export function getBeforeNumDay(
  days: number,
  startDate?: string | number | Date
): number {
  const date = startDate ? new Date(startDate) : new Date();
  date.setDate(date.getDate() - days);
  return date.getTime();
}

// 获取相对于当前时间的后几天
export function getAfterNumDay(
  date: string | number | Date,
  days: number
): number {
  const curDate = new Date(date);
  curDate.setDate(curDate.getDate() + days);
  return curDate.getTime();
}

/**
 * 获取本月的开始时间和结束时间（时间戳）
 * @param {Date} date 基准日期，默认当前日期
 * @returns {[number, number]} 返回 [开始时间戳, 结束时间戳]
 */
export function getCurrentMonthTimestampRange(
  date: string | number | Date = new Date()
): [number, number] {
  const currentDate = dayjs(date);
  const startOfMonth = currentDate.startOf('month').valueOf();
  const endOfMonth = currentDate.endOf('month').valueOf();
  return [startOfMonth, endOfMonth];
}

/* 传给后端的时间范围是要把结束时间完后推一天，表示包含当天的时间范围
开始时间往前推8小时，结束时间往后推16小时（表示包含当天） */
export const timeZoneBack = (
  format?: string,
  start = new Date(),
  end = new Date()
) => {
  start = getNewDate(start);
  end = getNewDate(end);
  start.setHours(start.getHours() - 8);
  end.setHours(end.getHours() + 16);
  if (format) {
    return [dayjs(start).format(format), dayjs(end).format(format)];
  }
  return [start, end];
};

export function isDev() {
  return (
    window.location.host.indexOf('devscm') > -1 ||
    window.location.host.indexOf('localhost') > -1 ||
    window.location.host.indexOf('127.0.0.1') > -1
  );
}

export function isLocal() {
  return (
    window.location.host.indexOf('localhost') > -1 ||
    window.location.host.indexOf('127.0.0.1') > -1
  );
}
/**
 * 获取几天后的时间
 * @param {n} 天数
 * @param {Date} date
 */
export function getAfterDate(
  n: number,
  date: string | number | Date = new Date(),
  zero = true
): number {
  return (
    new Date(zero ? getBeginningTime(date) : date).getTime() +
    n * 3600 * 24 * 1000
  );
}

/**
 *
 * @param {string | number | Date | Dayjs} dateType 日期
 * @param {string} format 日期格式
 * @param {string} placeHolder 占位符
 * @returns
 */
export function formatTime(
  date: string | number | Date,
  format = 'YYYY-MM-DD HH:mm',
  placeHolder = '-'
): string {
  if (!date) return placeHolder;
  return dayjs(date).format(format);
}

// 获取包含今天在内的近几天（可指定格式）
export const getRecentDaysFormatted = (numDays = 7, format = 'YYYY-MM-DD') => {
  const today = new Date();
  const dates = [];

  for (let i = numDays - 1; i >= 0; i -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    dates.push(formatTime(date, format));
  }

  return dates;
};

export function isObject(value: any) {
  return typeof value === 'object' && value !== null;
}

/**
 * @param {*} dataAttrIdKey 'sizeId'
 * @param {*} dataAttrNameKey 'sizeName'
 * @param {*} dataList [{sizeName: 'XL', sizeId: 12}]
 */
export function handleCommonList(
  dataAttrIdKey: string,
  dataAttrNameKey: string,
  dataList: any[] = []
) {
  let list: any = [];
  dataList.forEach((item: any) => {
    // XL
    const attrIdValue = item[dataAttrIdKey];
    // 12
    const attrNameValue = item[dataAttrNameKey];
    // 排除相同的
    if (list.every((data: any) => data.id !== attrIdValue)) {
      list = [
        ...list,
        {
          id: attrIdValue,
          name: attrNameValue,
        },
      ];
    }
  });
  return list;
}

/**
 * @param {Array} dataList [{colorName: '红色', colorId: 21, sizeName: 'XL', sizeId: 18, ...}]
 * @param {*} keys
 * @param {*} addKeys
 * @param {*} keyIncludeText key是否包含文本
 * @returns Array
 */
export function handleTwoIdList(
  dataList: any = [],
  keys: any = {},
  addKeys: any = ['skuBarCode', 'id'],
  keyIncludeText = false
) {
  const list: any = {};
  const defaultKes = {
    firIdKey: 'colorId',
    firIdVal: 'colorName',
    secIdKey: 'sizeId',
    sedIdVal: 'sizeName',
  };
  keys = Object.assign(defaultKes, keys);
  const { firIdKey, firIdVal, secIdKey, sedIdVal } = keys || defaultKes;
  dataList.forEach((item: any) => {
    const firIdKeyData = item[firIdKey]; // 21
    const firIdValData = item[firIdVal]; // 红色
    const secIdKeyData = item[secIdKey]; // 18
    const sedIdValData = item[sedIdVal]; // XL
    const temp = {
      [`${firIdKey}`]: firIdKeyData,
      [`${firIdVal}`]: firIdValData,
      [`${secIdKey}`]: secIdKeyData,
      [`${sedIdVal}`]: sedIdValData,
    };
    const keyName = keyIncludeText
      ? `${firIdKeyData}_colorId`
      : `${firIdKeyData}`;
    if (Object.keys(list).some((firId: any) => `${firId}` === keyName)) {
      Object.entries(list).forEach((firMapSec: any) => {
        const firId = firMapSec[0];
        addKeys.forEach((key: any) => {
          temp[key] = item[key];
        });
        if (`${firId}` === keyName) {
          firMapSec[1][`${secIdKeyData}`] = temp;
        }
      });
    } else {
      addKeys.forEach((key: any) => {
        temp[key] = item[key];
      });
      list[keyName] = {
        [`${secIdKeyData}`]: temp,
      };
    }
  });
  return list;
}

/**
 * @param {Array} dataList [{colorName: "红色", sizeName: 'XL', number: 15}]
 * @param {string} columnFileKey 首列 column Filed 如 color
 * @param {string} columnValKey  首列 column Filed 使用的 dataList 中的key 如 colorName
 * @param {string} rowAttrKey 非首列 使用的dataList 中的属性的key 如 sizeId
 * @param {string} rowValKey  非首列 使用的dataList 中的值的key 如 number
 * @param {any} defRowValKey rowValKey 为 undefined 的替代品
 * @returns Array
 */
export function handleTableData(
  dataList: Record<string, any>[] = [],
  columnFileKey = 'color',
  columnValKey = 'colorName',
  rowAttrKey = 'sizeId',
  rowValKey = 'number',
  defRowValKey: string | number | undefined = '',
  isCopy = false // isCopy为true时将不会对data的rowKey赋值（不会执行data[rowKey] = rowVal;
) {
  let tableData: any = [];
  dataList.forEach((item: any) => {
    const columnVal = item[columnValKey];
    const columnId = `${columnFileKey}Id`;
    const rowKey = item[rowAttrKey];
    const rowVal = item[rowValKey] || defRowValKey;
    if (tableData.some((data: any) => data[columnFileKey] === columnVal)) {
      if (!isCopy) {
        tableData.forEach((data: any) => {
          if (data[columnFileKey] === columnVal) {
            data[rowKey] = rowVal;
          }
        });
      }
    } else {
      const itemTemp = {
        [columnFileKey]: columnVal,
        [rowKey]: rowVal,
        [columnId]: item[columnId],
      };
      if (isCopy) delete itemTemp[rowKey];
      tableData = [...tableData, itemTemp];
    }
  });
  return tableData;
}

// 相对于handleTableData，rowAttrs支持数组多个参，将会往同一行中添加对应多个键值对
export function handleTableDataV2(
  dataList: Record<string, any>[] = [],
  columnFileKey = 'color',
  columnValKey = 'colorName',
  rowAttrs = [
    { key: 'sizeId', valKey: 'number', colPrefix: '', defaultVal: '' },
  ],
  isCopy = false // isCopy为true时将不会对data的rowKey赋值（不会执行if (!isCopy)）;
) {
  let tableData: any = [];
  dataList.forEach((item: any) => {
    const columnVal = item[columnValKey];
    const columnId = `${columnFileKey}Id`;
    const curRow = tableData.find(
      (data: any) => data[columnFileKey] === columnVal
    );
    const itemTemp = curRow ?? {
      [columnFileKey]: columnVal,
      // [rowKey]: rowVal,
      [columnId]: item[columnId],
    };
    if (!isCopy) {
      rowAttrs.forEach((config: Recordable) => {
        const { key, valKey, colPrefix, defaultVal } = config;
        itemTemp[`${colPrefix}${item[key]}`] = item[valKey] || defaultVal;
      });
    }
    if (!curRow) {
      // 新加的
      tableData = [...tableData, itemTemp];
    }
  });
  return tableData;
}

/**
 * 根据sku列表找出color和size的id->name的map对象
 * @param dataList
 * @returns
 */
export function handleColorAndSizeMap(dataList: Record<string, any>[] = []) {
  const color: Record<string, any> = {};
  const size: Record<string, any> = {};
  dataList.forEach((sku: Record<string, any>) => {
    const { colorId, colorName, sizeId, sizeName } = sku;
    if (colorId && !color?.[colorId]) color[colorId] = colorName;
    if (sizeId && !size?.[sizeId]) size[sizeId] = sizeName;
  });
  return { color, size };
}

export function getFileType(text: any) {
  let type;
  switch (text) {
    case 'image':
      type = 1;
      break;
    case 'video':
      type = 2;
      break;
    default:
      type = 3;
      break;
  }
  return type;
}

// 获取文件后缀名称
export function getFileSuffix(name: any) {
  let fileSuffix = '';
  if (name?.split) {
    fileSuffix = name?.split('.').pop();
  }
  return fileSuffix;
}
export function getTypeBySuffix(suffix: string) {
  const imageSuffix = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'webp',
    'svg',
  ];
  const videoSuffix = ['mp4', 'avi', 'wmv', 'flv', 'mov', 'mkv', 'mpeg', 'mpg'];
  suffix = typeof suffix === 'string' ? suffix.toLowerCase() : suffix;
  if (imageSuffix.includes(suffix)) {
    return 'image';
  }
  if (videoSuffix.includes(suffix)) {
    return 'video';
  }
  return suffix;
}

export const renderFileIcon = (file: Recordable) => {
  if (!file.name) {
    file.name = file.fileName;
  }
  let type = 'file';
  if (file.file && file.file.type) {
    type = file.file.type;
  } else {
    let extension = file.name?.split('.')[1] ?? '';
    if (!extension && file.url) {
      const arr = file.url?.split('.');
      extension = arr[arr.length - 1];
    }
    extension = extension?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'].includes(extension)) {
      type = 'image';
    } else if (['mp4', 'm2v', 'mkv', 'm4v', 'mov'].includes(extension)) {
      type = 'video';
    } else if (
      ['mp3', 'wav', 'wmv', 'm4a', 'acc', 'flac'].includes(extension)
    ) {
      type = 'audio';
    } else if (isImageUrl(file.url)) {
      type = 'image';
    }
  }

  return type;
};

/**
 * 处理附件
 * @param {Array} list 附件列表
 * @param {Object} params 需要给附件额外加上的参数，如单号，物料Id等
 * @returns Array
 */

export function handleFile(
  list: Record<string, any>[],
  params: Record<string, any> = {}
) {
  return handleFileOrImages(list, 'file', params);
}
/**
 * 处理图片
 * @param {Array} list 图片列表
 * @param {Object} params 需要给图片额外加上的参数，如单号，物料Id等
 * @returns Array
 */

export function handleImages(
  list: Record<string, any>[],
  params: Record<string, any> = {}
) {
  return handleFileOrImages(list, 'images', params);
}

/**
 * 处理图片 OR 附件
 * @param {Array} list 图片或附件列表
 * @param {Object} params 需要给图片额外加上的参数，如单号，物料Id等
 * @returns Array
 */
export function handleFileOrImages(
  list: Record<string, any>[],
  handleType = 'images',
  params: Record<string, any> = {}
) {
  if (!Array.isArray(list)) return [];
  if (!isObject(params)) {
    params = {};
  }
  const tempLlist: Record<string, any>[] = [];
  list.forEach((item, index) => {
    const { fileId, name, fileName, url, id, type } = item;
    const temp: Record<string, any> = {
      type,
      fileId,
      url,
      showOrder: index,
    };
    if (handleType === 'images') {
      temp.name = name || fileName;
    }
    if (handleType === 'file') {
      temp.fileName = fileName || name;
    }
    if (id || id === 0) {
      temp.id = id;
    }
    Object.assign(temp, params);
    tempLlist.push(temp);
  });
  return tempLlist.filter((e) => Boolean(e.url));
}
/**
 * 检查附件列表是否存在“上传中”状态的附件
 * @param {Array} list
 * @returns Boolean
 */
export function checkUploaderStatus(
  list: Record<string, any>[],
  showToast = true
) {
  if (list.some((file) => file?.status === STATUS.fail)) {
    if (showToast === true) Message.error('附件上传失败，请检查！');
    return true;
  }
  const status = list.some((file) => file?.status === STATUS.uploading);
  if (status && showToast === true) {
    Message.warning('附件上传中！');
  }
  return status;
}
/*
 * @param {Arrat} dataList [{colorName: "红色", sizeName: 'XL', number: 15}...]
 * @param {string} dataAttrKey data 中的key，其data[key] 作为 field 的 value
 * @param {Object} firstColumn 首列
 * @param {Object} lastColumn 最后一列
 * @returns Array
 */
export const handleTableColumns = (
  dataList: any,
  dataAttrKey: string,
  firstColumn: object | undefined,
  lastColumn: object | undefined,
  columnIdKey: any,
  idKeys = ['id'],
  extraColumnField: Record<string, any> | undefined = {}
) => {
  let columns: any = [];
  // 如果存在首列
  if (firstColumn) {
    columns = [firstColumn];
  }
  dataList.forEach((item: any) => {
    const columnKey = item[dataAttrKey];
    const columnId = `${item[columnIdKey]}`;
    const temp: any = {
      field: columnId,
      key: columnId,
      title: columnKey,
      width: 135,
      default: 0,
    };
    Object.assign(temp, extraColumnField);
    idKeys.forEach((key: any) => {
      temp[`${key}`] = item[`${key}`];
    });
    // 如果没有存在此列
    if (columns.every((data: any) => data.field !== columnId)) {
      columns = [...columns, temp];
    }
  });
  // 如果存在最后一列
  if (lastColumn) {
    const temp = Array.isArray(lastColumn) ? lastColumn : [lastColumn];
    columns = [...columns, ...temp];
  }
  return columns;
};

// 删除中文
export const removeChineseText = (value: string) =>
  value.replace(/[\u4E00-\u9FA5]/g, '');

// 只允许输入正整数
export const onlyPositiveInteger = (value: any) => {
  let result = value;
  if (typeof result === 'number') {
    result = String(value);
  }
  result = Number(result.replace(/\D/g, '')) as number;
  // 输入不可以大于最大整数
  if (result > globalContants.MAXNINEDIGITNUM) {
    result = globalContants.MAXNINEDIGITNUM;
  }
  return result;
};

// 选择地址统一已处理
export const areaList = (function areaJsonFn() {
  const areaListTmp: any = {
    province_list: {},
    city_list: {},
    county_list: {},
  };
  areaJson.forEach((item: any) => {
    areaListTmp.province_list[item.id] = item.areaName;
    item.children.forEach((city: any) => {
      areaListTmp.city_list[city.id] = city.areaName;
      city.children?.forEach((county: any) => {
        areaListTmp.county_list[county.id] = county.areaName;
      });
    });
  });
  return areaListTmp;
})();

/**
 * 判断是否为正确的手机号码
 * @param cellPhoneNo 手机号
 * @param prefix 前缀
 * @returns {*|boolean}
 */
export function isValidCellPhoneNo(cellPhoneNo: any, prefix?: any) {
  let regExp: any = '';
  if (prefix === '' || prefix === undefined) {
    regExp = /^1[3456789]\d{9}$/;
  } else {
    regExp = /^0?[0-9]{8,10}$/;
  }
  return regExp.test(cellPhoneNo);
}

// 支持负数
export function formatterNegative(value: any, decimal: string | number = 2) {
  return formatter(value, decimal, true);
}

// 不支持负数
export function formatter(
  value: any,
  decimal: string | number,
  negative?: boolean, // 是否能格式化负数
  options?: Recordable
) {
  options = {
    formatterRound: false,
    ...options,
  };
  if (value === undefined) return undefined;
  if (decimal === undefined) decimal = 2;
  if (typeof value !== 'string') value = value.toString();
  // 如果第一位是0且第二位不是小数点，则去掉0
  if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
    value = value.substring(1);
  }
  if (value || value === '0') {
    // 不能为负数
    if (!negative && /^-/.test(value)) {
      value = value.substring(1);
    }
    let temp: number;
    if (options.formatterRound) {
      temp = formatterRound(Number(value), decimal as number) as number;
    } else {
      // 如果有小数位
      if (value.indexOf('.') !== -1) {
        value = value.substring(0, value.indexOf('.') + decimal + 1);
      }
      temp = Number(value);
    }

    // 输入不可以大于最大整数
    if (temp > globalContants.MAXNINEDIGITNUM) {
      value = globalContants.MAXNINEDIGITNUM;
    }
    return temp;
  }
  return undefined;
}

// 四舍五入格式化小数点
export function formatterRound(
  value: any,
  decimal: number,
  negative?: boolean // 是否能格式化负数
) {
  if (decimal === undefined) decimal = 2;
  const decimalOneMorePerson = decimal + 1; // 多加一位是为了四舍五入
  const formatterValue = formatter(value, decimalOneMorePerson, negative);
  if (formatterValue === undefined) return undefined;
  return roundDecimal(formatterValue, decimal); // 使用实际的小数位四舍五入
}

// 按配置小数位和四舍五入格式化金额
export const priceDecimalFormatter = ({ cellValue }: any): string => {
  if (cellValue === 0) return `${globalContants.CURRENCYSYMBOL}0`;
  if (!cellValue) return `-`;
  return `${globalContants.CURRENCYSYMBOL}${formatterRound(
    cellValue,
    materialDecimal.value
  )}`;
};

export const setPriceSymbol = (
  value: any,
  symbol: string = globalContants.CURRENCYSYMBOL
) => {
  if (value === 0) return `${symbol}0`;
  if (!value) return '空';
  return `${symbol}${value}`;
};

// 获取选择地址json
export const selectAreaData = (function getdata() {
  const areaJsonList = cloneDeep(areaJson);
  const deepChildren = (list: any[]) => {
    list.forEach((item: any) => {
      item.value = item.id;
      item.label = item.areaName;
      delete item.id;
      delete item.areaName;
      if (item?.children?.length) {
        deepChildren(item.children);
      } else {
        delete item.children;
      }
    });
  };
  deepChildren(areaJsonList);
  return areaJsonList;
})();
export const getAddress = (areaObj: Recordable, hasAddress = false) => {
  // 省 市 区 详细地址
  const {
    recipientProvincesId,
    recipientCityId,
    recipientCountyId,
    address = '',
  } = areaObj;
  // 地区数据回显成字符串
  let area = '';
  const province = areaList.province_list[recipientProvincesId];
  const city = areaList.city_list[recipientCityId];
  const county = areaList.county_list[recipientCountyId];
  if (!province) {
    area = '';
  } else if (!city) {
    area = province;
  } else if (!county) {
    area = `${province}-${city}`;
  } else {
    area = `${province}-${city}-${county}`;
  }
  if (address && hasAddress) {
    area = area ? `${area},${address}` : address;
  }
  return area || '';
};

// 发货方式转文字
export const handleDeliveryInfo = (
  val: Recordable,
  fields: string[] = [
    'contactName',
    'contactPhone',
    'areaName',
    // 'recipientCityName',
    // 'recipientCountyName',
    'toPoint',
  ]
) => {
  if (!val) return '';
  if (val.deliveryType === 1) return '自提';
  val.areaName = getAddress(val);
  const list: string[] = [];
  fields.forEach((field: string) => {
    if (val[field]) {
      list.push(val[field]);
    }
  });
  return list.join('-');
};

/**
 * 截取两位小数
 * @param value 输入的数字 或可转数字的字符串 2.136 => 2.13 5 => 5
 */
export function cutTwoDecimal(value: string | number | undefined) {
  return cutDecimal(value, 2);
}
export function cutDecimal(
  value: string | number | undefined,
  decimal?: number,
  isNegative?: boolean // 是否支持格式化负数，之前不支持
) {
  if (value === undefined) return 0;
  decimal = decimal || materialDecimal.value;
  // eslint-disable-next-line no-useless-escape
  const reg = isNegative
    ? RegExp(`^-?\\d+(?:\\.\\d{0,${decimal}})?`)
    : RegExp(`^\\d+(?:\\.\\d{0,${decimal}})?`);
  value = Number(value.toString().match(reg));
  return value;
}

// 支持格式化负数
export function cutNegativeDecimal(
  value: string | number | undefined,
  decimal?: number
) {
  return cutDecimal(value, decimal, true);
}

/**
 * 四舍五入截取两位小数
 * @param value 输入的数字 或可转数字的字符串 2.136 => 2.14
 */
export function roundTwoDecimal(value: string | number | undefined) {
  return roundDecimal(value, 2);
}

export function roundDecimal(
  value: string | number | undefined,
  decimal?: number
) {
  return roundNumber(value, decimal ?? materialDecimal.value);
}

// 四舍五入，默认格式化整数
export function roundNumber(value: string | number | undefined, decimal = 0) {
  if (Number.isNaN(value)) return 0;
  value =
    accDiv(
      Math.round(accMul(Number(value), 10 ** decimal) ?? 0),
      10 ** decimal
    ) ?? 0;
  return value;
}

// 是否是正常数字
export function isNormalNumber(value: unknown): boolean {
  // 类型过滤：非数值类型直接返回占位符
  if (typeof value !== 'number') return false;

  // 特殊数值检测
  if (Number.isNaN(value) || !Number.isFinite(value)) {
    return false;
  }

  return true;
}

export function getObject(arr: Recordable[], key: string, value: string) {
  if (!Array.isArray(arr)) return undefined;
  return arr.find((item: Recordable) => item[key] === value);
}

/**
 * 单据状态格式化过滤器
 * @param input 当前项的值
 * @param arr 所有值数组
 * @param keyName 传入input值对应的键名,默认值为'val'
 * @param valName 需要获取的值对应的键名,默认值为'text'
 * @return text  返回valName键名对应的值
 */
export function orderSource(
  input: any,
  type: any,
  keyName = 'val',
  valName = 'text'
) {
  if (input !== undefined) {
    const accType = ((orderState as Recordable)[order[type]] || []).find(
      (e: any) => e[keyName] === input
    );
    let showString = '';
    if (accType !== undefined) {
      showString = accType[valName];
    }
    return showString;
  }
  return input;
}

/**
 * 将金额的数值进行处理，每隔三位数字，用逗号分隔开，并且保留两位小数，默认加上系统货币符号
 * @param value 需要格式化的值
 * @param notCurrencySymbol 是否需要金额符号
 * @param decimal 保留位数
 * @return 返回格式化后的结果
 */
export function numberToCurrency(
  value: string | number,
  notCurrencySymbol = false,
  decimal = 2,
  removeTrailingZeros = false
) {
  let symbol = globalContants.CURRENCYSYMBOL;
  let result = '';
  let addCode = '';
  if (notCurrencySymbol) {
    // 有值则说明不需要货币符号
    symbol = '';
  }
  if (!value) {
    result = '0.00';
  } else {
    const intPart = parseInt(value.toString(), 10) ?? 0; // 获取整数部分
    const stringVal = Object.is(intPart, -0) ? '-0' : intPart.toString();
    const intPartFormat = stringVal.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,'); // 将整数部分每三位加一个逗号
    let floatPart: string | number = '.'.padEnd(decimal + 1, '0'); // 预定义小数部分
    if (typeof value !== 'string') {
      value = value.toString();
    } else if (intPart > 0 && value.includes('+')) {
      value = value.replace('+', '');
      addCode = '+';
    }
    const value2Array = value.split('.');
    //= 2表示数据有小数位
    if (value2Array.length === 2) {
      floatPart = value2Array[1].toString(); // 拿到小数部分
      if (floatPart.length === 1) {
        // 若小数位数只有一位，补0
        result = `${intPartFormat}.${floatPart}0`;
      } else if (floatPart.length > decimal) {
        // 若小数位数在两位以上，则四舍五入保留两位小数
        floatPart = new BigNumber(`0.${floatPart}`)
          .toFixed(decimal)
          .replace('0.', '');
        // 如果是0.9966等类型四舍五入会变成1.00，此时单独处理
        // parseInt(floatPart, 10) === 1 —— 如果是0.008四舍五入为01 parseInt会为1,所以要区分floatPart为1.00、1.000、01、001的情况
        const isBigDecimal = floatPart.toString()[0] === '1';
        result =
          parseInt(floatPart, 10) === 1 && isBigDecimal
            ? (accAdd(stringVal || 0, 1)?.toString() as string)
            : `${intPartFormat}.${floatPart}`;
      } else {
        result = `${intPartFormat}.${floatPart}`;
      }
    } else {
      // 数据为整数
      result = intPartFormat + floatPart;
    }
  }
  if (removeTrailingZeros) {
    result = removeTrailingZerosFn(result);
  }
  return symbol + addCode + result;
}

// 用于去除小数点后面尾部的0
export function removeTrailingZerosFn(value: string) {
  if (!value) return value;
  const str = value.toString();
  if (str.indexOf('.') === -1) {
    return value;
  }
  let trimmed = str.replace(/0+$/, '');
  if (trimmed.endsWith('.')) {
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed;
}
/**
 * 将金额的数值进行处理，每隔三位数字，用逗号分隔开，并且保留两位小数，默认加上系统货币符号
 * @param amount 需要格式化的值
 * @param notCurrencySymbol 是否需要金额符号
 * @param decimal 保留位数
 * @return 返回格式化后的结果
 */
export function formatAmount(
  amount: number,
  notCurrencySymbol = false,
  decimalPlaces = 2
): string {
  let symbol = globalContants.CURRENCYSYMBOL;
  if (notCurrencySymbol) {
    // 有值则说明不需要货币符号
    symbol = '';
  }
  const formattedAmount = new BigNumber(amount).toFixed(decimalPlaces); // 保留指定位数的小数
  const parts = formattedAmount.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 正则表达式添加千分位逗号
  if (decimalPlaces > 0) {
    return `${symbol}${integerPart}.${parts[1]}`;
  }
  return `${symbol}${integerPart}`;
}

/* 组件date选出来的时间是0点，产品要新增的时候只选年月日，详情又要显示为年月日和当前的时间
 * @param {Date} businessTime
 * @returns 日期与businessTime相同，时间为当前时间的Date
 */
export const replaceWithCurrentTime = (businessTime: string | Date) => {
  const date = new Date(businessTime);
  const now = new Date();
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  );
};

// 深拷贝对象
export function deepClone(obj: any) {
  const toStringFn = Object.prototype.toString;

  // null, undefined, non-object, function
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  // DOM节点
  if (obj.nodeType && 'cloneNode' in obj) {
    return obj.cloneNode(true);
  }

  // Date对象
  if (toStringFn.call(obj) === '[object Date]') {
    return new Date(obj.getTime());
  }

  // RegExp对象
  if (toStringFn.call(obj) === '[object RegExp]') {
    const flags = [];
    if (obj.global) {
      flags.push('g');
    }
    if (obj.multiline) {
      flags.push('m');
    }
    if (obj.ignoreCase) {
      flags.push('i');
    }

    return new RegExp(obj.source, flags.join(''));
  }

  let result: any;
  if (Array.isArray(obj)) {
    result = [];
  } else if (obj.constructor) {
    result = new obj.constructor();
  } else {
    result = {};
  }

  Object.keys(obj).forEach((key: string) => {
    result[key] = deepClone(obj[key]);
  });

  return result;
}

// 检查是否有权限
export const checkAuthority = (
  roles: string[] | string | string[][],
  permissionMode: RouteMeta['permissionMode'] = 'every',
  toast = false
) => {
  if (!roles?.length) return true;
  const authorityList = Array.isArray(roles) ? roles : [roles];
  const { permissions = [], permissionRemarkMap = {} } = userStore;
  if (
    Object.keys(permissionRemarkMap)?.length === 0 &&
    authorityList.length &&
    !isFactory()
  ) {
    // 说明获取不到权限则清除用户信息，再下次跳转路由时会再次getCurrentUser获取用户信息
    userStore.resetInfo();
  }
  let check = true;
  let noAuthorityList: string[] = [];
  if (
    permissionMode === 'general' ||
    (authorityList.length && Array.isArray(authorityList[0]))
  ) {
    // general模式 || authorityList为二维数组
    // general模式下roles是一个字符串二维数组，需要其中存在一项数组的权限全满足时代表有权限
    check = (roles as string[][]).some((arr: string[]) => {
      const noAuthByItem = arr.filter((item: string) => {
        return !permissions.includes(item);
      });
      if (noAuthByItem.length) {
        noAuthorityList.push(...noAuthByItem);
        return false;
      }
      return true;
    });
    if (!check) {
      noAuthorityList = uniq(noAuthorityList);
    }
  } else {
    noAuthorityList = (authorityList as string[]).filter(
      (item: string) => !permissions.includes(item)
    );
    check =
      permissionMode === 'every'
        ? noAuthorityList.length === 0
        : noAuthorityList.length < authorityList.length;
  }
  if (!check && toast === true) {
    if (
      permissionMode === 'general' ||
      (authorityList.length && Array.isArray(authorityList[0]))
    ) {
      const hasAuthArr = (roles as string[][]).filter((arr: string[]) =>
        arr.some((item: string) => !noAuthorityList.includes(item))
      );
      if (hasAuthArr.length) {
        // 二维数组中有一些组是存在有权限的字符串
        const noAuthArr = uniq(
          hasAuthArr.map((arr: string[]) =>
            arr
              .filter((item: string) => noAuthorityList.includes(item))
              .map((e) => permissionRemarkMap[e])
              .join('、')
          )
        );
        // 找出这些组中没有权限的字符串，只提示缺少的项
        Message.error(
          `权限不足，请联系管理员设置「${uniq(noAuthArr).join(' | ')}」权限`
        );
        return check;
      }
    }
    Message.error(
      `权限不足，请联系管理员设置「${uniq(
        noAuthorityList.map((e) => permissionRemarkMap[e])
      ).join(permissionMode ? ' | ' : '、')}」权限`
    );
  }
  return check;
};

// 检查是否有权限并提示
export const checkAuthorityToast = (
  roles: string[] | string | string[][],
  permissionMode: RouteMeta['permissionMode'] = 'every'
) => {
  return checkAuthority(roles, permissionMode, true);
};

export const checkSysConfigToast = (key: string | undefined) => {
  const { configMap = {}, sysConfigList = [] } = supplierStore;
  const value = configMap[key as string];
  const check = value === '1';
  const configInfo: any = sysConfigList.find((e: any) => e.configKey === key);
  if (!check) {
    Message.error(
      `请开启${configInfo?.name || factorySysConfigMap[key as string]}系统配置`
    );
  }
  return check;
};

// 将一个sheet转成最终的excel文件的blob对象，然后利用URL.createObjectURL下载
export const sheet2blob = (sheet: any, sheetName = 'sheet1') => {
  const workbook: {
    SheetNames: string[];
    Sheets: any;
  } = {
    SheetNames: [sheetName],
    Sheets: {},
  };
  workbook.Sheets[sheetName] = sheet;
  // 生成excel的配置项
  const wopts = {
    bookType: 'xlsx', // 要生成的文件类型
    bookSST: false, // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
    type: 'binary',
  };
  const wbout = window.XLSX.write(workbook, wopts);
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });

  // 字符串转ArrayBuffer
  function s2ab(s: string) {
    const buf = new ArrayBuffer(s.length * 2);
    const view: any = new Uint8Array(buf);
    let i = 0;
    while (i < s.length) {
      view[i] = s.charCodeAt(i);
      i += 1;
    }
    return buf;
  }

  return blob;
};

// 导出excel文件
export const exportDataToExcel = (exportList: any[], fileName: string) => {
  const sheet = window.XLSX.utils.json_to_sheet(exportList);
  const url: any = sheet2blob(sheet);
  if (typeof url === 'object' && url instanceof Blob) {
    const str = `${fileName}.xlsx`;
    const filename = decodeURIComponent(str);
    // 创建一个临时的url指向blob对象
    const objectUrl = window.URL.createObjectURL(url);
    // 创建url之后可以模拟对此文件对象的一系列操作，例如：预览、下载
    const a = document.createElement('a');
    a.setAttribute('href', objectUrl);
    a.setAttribute('download', filename);
    a.click();
    // 5.释放这个临时的对象url
    window.URL.revokeObjectURL(objectUrl);
  }
};
// 生成随机数id
export const generateRandomId = () => {
  return `id-${new Date().getTime().toString(36)}-${Math.random()
    .toString(36)
    .substring(2, 9)}`;
};

// 替换图片别名
export const replacePicAlias = (imgurl: string, alias: string) => {
  if (!imgurl) return imgurl;
  if (
    imgurl?.split('.')[1] &&
    imgurl?.split('.')[1]?.indexOf('x-image-process') > -1
  ) {
    return imgurl.replace(
      /([?&]x-image-process=)style\/[^&#]*/i,
      `$1style/${encodeURIComponent(alias)}`
    );
  }
  return dealWithPic(imgurl, alias);
};

// 压缩图片：pclist goodalbum pic200wh等
export function dealWithPic(imgurl: string, alias: string) {
  if (!imgurl) return imgurl;
  // 已经使用了别名的图片
  if (
    imgurl?.split('.')[1] &&
    (imgurl?.split('.')[1]?.indexOf('x-image-process') > -1 ||
      imgurl.split('.')[1].indexOf('@!') > -1)
  ) {
    return imgurl;
  }
  if (imgurl?.indexOf('.gif') > -1) {
    // auto-orient 会使gif动效失效，所以gif不进行图片处理
    return imgurl;
  }
  // 使用别名的规则进行压缩图片-SCM图片
  if (
    (imgurl?.indexOf('is-scm-dev.cdn.qinsilk.com') > -1 ||
      imgurl?.indexOf('is-scm.cdn.qinsilk.com') > -1) &&
    alias
  ) {
    if (imgurl?.indexOf('?') === -1) {
      return `${imgurl}?x-image-process=style/${alias}`;
    }
    return imgurl;
  }
  // 使用别名的规则进行压缩图片-进销存图片（同步进销存商品）
  if (imgurl?.indexOf('qinsilk.com') > -1 && alias) {
    if (imgurl?.indexOf('?') === -1) {
      return `${imgurl}@!${alias}`;
    }
    return imgurl;
  }
  return imgurl;
}

// 判断当前页面有没有打开的弹窗
export const isUseModal = () => {
  return (
    Array.from(document.querySelectorAll('.arco-modal-container'))?.some(
      (e: Recordable) => {
        return e?.style?.display !== 'none';
      }
    ) ?? false
  );
};

// 根据字段对数组分组
export const groupByField = (list: Recordable[], field: string) => {
  return list.reduce((acc, item) => {
    const key = item[field];
    if (acc[key]) {
      acc[key].push(item);
    } else {
      acc[key] = [item];
    }
    return acc;
  }, {});
};

// 根据系统配置显示和隐藏菜单入口
export const showMenuBySycConfig = (
  sysConfigMap: Recordable,
  menu: RouteMeta
) => {
  if (!menu) return true;
  const { sysConfigKey, defaultValue = true } = menu;
  if (!sysConfigKey) return true;
  const numberValue = Number(sysConfigMap[sysConfigKey]);
  const value = Number.isNaN(numberValue) ? defaultValue : numberValue;
  return !!value;
};

// 用户div等元素使用快捷键模拟点击（比如div获取焦点后使用enter模拟点击）
export const imitateTheClickEnterByKeydown = (
  e: any,
  querySelectorStr?: string,
  KEY = KEYBOARD_KEY.ENTER
) => {
  if (e?.key === KEY) {
    e?.preventDefault();
    const targetEl = querySelectorStr
      ? e?.target?.querySelector(querySelectorStr)
      : e?.target;
    targetEl?.dispatchEvent(new Event('click'));
  }
};
// format 默认精度为天数
export const getDiffDate = (
  date: Date,
  target: Date | number = new Date(),
  format = 'YYYY-MM-DD'
) => {
  const src = dayjs(date).format(format);
  const nowDayjs = dayjs(target).format(format);
  return dayjs(src).diff(dayjs(nowDayjs), 'day');
};

// 相对当前时间多久，几秒前 几分钟前...
export const fromNowDiff = (time: Date, withoutSuffix?: boolean) => {
  const date = new Date(time);
  return dayjs(date).locale('zh-cn').fromNow(withoutSuffix).replace(' ', '');
};

// 判断撤销抵冲状态
export const getStateInfo = (isRevoke: any, recordType: any) => {
  return StateInfo.find((item: Recordable) =>
    isRevoke === 0
      ? item.isRevoke === isRevoke
      : item.isRevoke === isRevoke && item.recordType === recordType
  );
};

// 处理年份的加减
export const handleYearInfo = (method: string, goodsInfo: Recordable) => {
  if (method === 'decrement') {
    if (goodsInfo.year === '' || goodsInfo.year === undefined) {
      goodsInfo.year = new Date().getFullYear();
    }
    if (goodsInfo.yearRanges === '' || goodsInfo.yearRanges === undefined) {
      goodsInfo.yearRanges = 1;
    }
    if (goodsInfo.yearRanges === 1) {
      goodsInfo.year = accSub(goodsInfo.year, 1, null);
      goodsInfo.yearRanges = 2;
    } else if (goodsInfo.yearRanges === 2) {
      goodsInfo.yearRanges = 1;
    }
  } else if (method === 'increment') {
    if (goodsInfo.year === '' || goodsInfo.year === undefined) {
      goodsInfo.year = new Date().getFullYear();
    }
    if (goodsInfo.yearRanges === '' || goodsInfo.yearRanges === undefined) {
      goodsInfo.yearRanges = 1;
    }
    if (goodsInfo.yearRanges === 1) {
      goodsInfo.yearRanges = 2;
    } else if (goodsInfo.yearRanges === 2) {
      goodsInfo.year = accAdd(goodsInfo.year, 1, null);
      goodsInfo.yearRanges = 1;
    }
  }
};

// 正则精确验证分数小数整数的情况
export const fractionValidate = (value: string) => {
  // 处理分数的情况
  const regex = /^-?\d+\/\d+$|^-?\d+\s?\d+\/\d+$|^-?\d+$|^-?\d+\.\d+$/;
  return regex.test(value);
};
export const getError = (value: any) => {
  if (value) {
    return !fractionValidate(value);
  }
  return false;
};
export function String2ArrayBuffer(str: any) {
  return new Promise((resolve, reject) => {
    const b = new Blob([str]);
    const f = new FileReader();
    f.onloadend = function (e) {
      if (f.error) {
        reject(f.error);
      }
      resolve(f.result);
    };
    f.readAsArrayBuffer(b);
  });
}
// 获取单品价格的范围字符串
export const getPriceRangeBySkuList = (list: number[]) => {
  let minPrice = 0;
  let maxPrice = 0;
  let priceRange = '';
  let priceList: number[] = [];
  const skuPriceList = list.filter(isNumber);
  if (skuPriceList.length) {
    priceList = uniq(skuPriceList);
    priceList.sort((a, b) => a - b);
    if (priceList?.length > 1) {
      [minPrice] = priceList;
      maxPrice = priceList[priceList.length - 1];
      priceRange = `${globalContants.CURRENCYSYMBOL}${minPrice} ~ ${globalContants.CURRENCYSYMBOL}${maxPrice}`;
    } else if (priceList.length) {
      [minPrice] = priceList;
      [maxPrice] = priceList;
      priceRange = `${globalContants.CURRENCYSYMBOL}${minPrice}`;
    }
  }
  return { priceRange, priceList, minPrice, maxPrice };
};

// 物料列表/选择物料-拼接价格范围
export const getPriceRange = (material: Recordable) => {
  return materialPriceRange(material, 'price', 'maxSkuPrice', 'minSkuPrice');
};
// 物料列表/选择物料-拼接价格范围 采购单价
export const getPurchasePriceRange = (material: Recordable) => {
  return materialPriceRange(
    material,
    'materialPurchasePrice',
    'maxSkuPurchasePrice',
    'minSkuPurchasePrice'
  );
};
export const materialPriceRange = (
  material: Recordable,
  priceField: string,
  maxPriceField: string,
  minPriceField: string
) => {
  if (!material) return `${globalContants.CURRENCYSYMBOL}0`;
  const { enableSkuPrice } = material;
  const price = material[priceField];
  const maxSkuPrice = material[maxPriceField] ?? 0;
  const minSkuPrice = material[minPriceField] ?? 0;
  const isZero = (val: any) => {
    return val === 0 ? `${globalContants.CURRENCYSYMBOL}0` : undefined;
  };
  if (!enableSkuPrice)
    return (
      isZero(price) ??
      `${formatAmount(price, undefined, materialDecimal.value)}`
    );
  if (minSkuPrice === maxSkuPrice)
    return (
      isZero(minSkuPrice) ??
      `${formatAmount(minSkuPrice, undefined, materialDecimal.value)}`
    );
  const minPrice =
    isZero(minSkuPrice) ??
    formatAmount(minSkuPrice, undefined, materialDecimal.value);
  const maxPrice =
    isZero(maxSkuPrice) ??
    formatAmount(maxSkuPrice, undefined, materialDecimal.value);
  return `${minPrice} ~ ${maxPrice}`;
};

// 获取物料面料属性名称
export const getFabricTypeName = (fabricType?: number) => {
  if (fabricType === 1) return '针织';
  if (fabricType === 2) return '梭织';
  return undefined;
};

// 物料面料属性名称表格格式化
export const fabricTypeNameFormatter = (defaultValue = '-') => {
  return ({ cellValue }: Recordable) => {
    return `${getFabricTypeName(cellValue) || defaultValue}`;
  };
};

// 物料列表物料类型-面料属性
export const formatterMaterialTypeAndFabricType = ({ row }: any): string => {
  const { materialTypeName, fabricType } = row;
  const fabricTypeName = getFabricTypeName(fabricType);
  if (fabricTypeName) return `${materialTypeName}-${fabricTypeName}`;
  return materialTypeName;
};

/**
 * 根据多单位转换系数列表获取第一个需要的字段
 * @param unitConvertList materialUnitConvertList / materialSkuUnitConvertList
 * @returns 转换系数、采购单价、采购单位、净布价
 */
export const getUnitConvertFields = (unitConvertList: Recordable[]) => {
  const {
    convertRate,
    purchasePrice,
    cleanClothPrice,
    purchaseUnitId,
    purchaseUnitName,
  } = unitConvertList?.[0] ?? {};
  return {
    convertRate,
    purchasePrice,
    cleanClothPrice,
    purchaseUnitId,
    purchaseUnitName,
  };
};

// cid = 578 维克多客户要求工序附件数量增加到50
export const getAttachmentMaxCount = (cid: number) => {
  if (cid === 578) {
    return globalContants.CUSTOMER_ATTACHEMENT_MAX_COUNT;
  }
  return globalContants.ATTACHEMENT_MAX_COUNT;
};

/**
 * 去掉小数点后面多余的 0
 * @param val
 */
export const removeDecimalZero = (val: string) => {
  const regexp = /(?:\.0*|(\.\d+?)0+)$/;
  val = `${val}`.replace(regexp, '$1');
  return val;
};

export const newFeatureReminderModal = (updateReminderKey?: string) => {
  const reminderMsg: Recordable = {
    'newUpdateReminder-isHidePrice':
      '生产工序支持隐藏工序单价，单价隐藏后无权限账号将不能查看未结算的工序价格，同时工资汇总也不计算被隐藏部分的工序工资',
    'newUpdateReminder-includingLossNumber': () => {
      return h('div', {}, [
        h(
          'div',
          { class: 'f15 mt6' },
          '设计款信息、商品信息的物料bom模块支持显示物料“含损耗用量”及含损耗单件平均成本；ie核价模块“用料成本”由物料“含损耗用量”计算得出'
        ),
        h('div', null, '注意：'),
        h('div', null, '1、含损耗用量=物料用量*（1+损耗率）/（1-缩水率）'),
        h('div', null, '2、若物料开启阶梯损耗率，按未开启时的损耗率进行计算'),
      ]);
    },
    'newUpdateReminder-includingLossNumberPODT': () => {
      return h('div', {}, [
        h(
          'div',
          { class: 'f15 mt6' },
          '生产单信息的物料bom模块支持显示物料“含损耗用量”及含损耗单件平均成本；'
        ),
        h('div', null, '注意：'),
        h('div', null, '1、含损耗用量=物料用量*（1+损耗率）/（1-缩水率）'),
      ]);
    },
    'newUpdateReminder-1.61.0': () => {
      return h('div', {}, [
        h(
          'div',
          { class: 'f15 mt6' },
          '“版单审批”和“物料采购审批”支持多级审批，可为同一审批流程配置多个审批人'
        ),
        h('div', null, '注意：'),
        h('div', null, '1、可前往系统配置-审批配置进行流程设置'),
        h(
          'div',
          null,
          '2、采购审核的“待办任务规则”更新为审批配置的“通知设置”（将向有权限的审批人发送审批提醒）'
        ),
        h('div', null, '3、新增“审批”列表，可快捷查看处理审批相关消息'),
      ]);
    },
    'newUpdateReminder-syncMaterialBom-1.74.1': () => {
      return h('div', {}, [
        h(
          'div',
          { class: 'f15 mt6' },
          '新增【BOM同步】功能，可将商品/生产单物料清单BOM同步到其他生产单'
        ),
        h('div', null, '注意：'),
        h(
          'div',
          null,
          '1、需前往系统配置-角色权限为相关角色配置【BOM同步】功能'
        ),
      ]);
    },
    'newUpdateReminder-syncMaterialBom-designOrder-1.74.1': () => {
      return h('div', {}, [
        h(
          'div',
          { class: 'f15 mt6' },
          '新增【BOM同步】功能，可将设计款/版单物料清单BOM同步到其他版单'
        ),
        h('div', null, '注意：'),
        h(
          'div',
          null,
          '1、需前往系统配置-角色权限为相关角色配置【BOM同步】功能'
        ),
      ]);
    },
    // 'newUpdateReminder-updateMaterialInfo-1.74.1': () => {
    //   return h('div', {}, [
    //     h(
    //       'div',
    //       { class: 'f15' },
    //       '新增【物料信息更新】功能，可将物料信息更新到版单/设计款/商品/生产单物料BOM清单'
    //     ),
    //     h(
    //       'div',
    //       {
    //         class: 'mt6',
    //       },
    //       '注意：'
    //     ),
    //     h(
    //       'div',
    //       null,
    //       '1、需前往系统配置-角色权限为相关角色配置【物料信息更新】功能'
    //     ),
    //   ]);
    // },
  };
  if (!updateReminderKey || !reminderMsg[updateReminderKey]) return;
  // 用户点击版单/采购中台(用料中心)/物料采购/待办列表/审批列表
  // const versionStorageKey = 'newUpdateReminder-1.61.0';
  const newUpdateReminder = localStorage.getItem(updateReminderKey);
  if (newUpdateReminder !== 'true') {
    Modal.info({
      title: '新功能提醒',
      okText: '我已知晓',
      hideCancel: true,
      bodyStyle: { 'color': 'red', 'padding-left': '30px' },
      content: reminderMsg[updateReminderKey],
      onOk: () => {
        localStorage.setItem(updateReminderKey, 'true');
      },
    });
  }
};

// 根据面料属性判断是否不显示对应字段值
export const checkMaterialFabricTypeIsShow = (
  row: Recordable,
  field: string,
  otherFields?: string[]
) => {
  if (!row) return false;
  const { materialTypeName, fabricType } = row;
  if (materialTypeName !== '面料' || fabricType !== -1) return false;
  // 物料属性为“面料” 且未开启面料属性
  return [
    'fabricWidth', // 幅宽
    'gramWeight', // 克重
    'materialGramWeight', // 克重
    'materialMeterOutput', // 出米数
    'meterOutput', // 出米数
    'paperBucket', // 纸筒
    'emptyGap', // 空差
    'weight', // 净重
    'summaryWeight', // 净重
    'materialNetWeight', // 净重
    ...(otherFields ?? []),
  ].includes(field);
};
// 根据面料类型判断是否不显示对应字段值
export const checkMaterialFieldIsShow = (
  row: Recordable,
  field: string,
  otherFields?: string[]
) => {
  const { fabricType, isStockByCloth } = row;
  // fabricType = 1 针织 2 梭织
  // 如果是【针织/梭织】，则【匹数、纸筒、空差、净重、克重、出米数】数据支持修改；
  if (![1, 2].includes(fabricType)) return false;
  // 针织、梭织都支持修改克重
  if (['gramWeight', 'materialGramWeight', 'fabricWidth'].includes(field))
    return true; // 支持修改幅宽
  // 如果是梭织，可以修改安数
  if (field === 'qunce') return fabricType === 2;
  if (field === 'cloth' && isStockByCloth) return true; // 开启了按匹的可以输入匹
  // 针织显示匹数、克重、出米数、纸筒、空差、净重
  return (
    fabricType === 1 &&
    [
      'cloth', // 匹数
      'gramWeight', // 克重
      'materialGramWeight', // 克重
      'materialMeterOutput', // 出米数
      'meterOutput', // 出米数
      'paperBucket', // 纸筒
      'emptyGap', // 空差
      'weight', // 净重
      'summaryWeight', // 净重
      'materialNetWeight', // 净重
      ...(otherFields ?? []),
    ].includes(field)
  );
};

export const toFraction = (val: number | string) => {
  return new Fraction(val).simplify().toFraction(true);
};
export const toFloat = (val: string, decimal: string | number = 2) => {
  return formatter(new Fraction(val).valueOf(), decimal, false);
};

export const getDiffDayNotAbs = (start: number, end: number) => {
  return (accSub(start, end) as number) / 1000 / 60 / 60 / 24;
};

// 物料领料/发料-设置物料预裁数相关信息
export const setMaterialSkuInfo = async (
  orderInfo: Recordable,
  list: any[],
  preCutNumField = 'issuedPreCutNumber',
  isSelectPro = false
) => {
  const { businessSn } = orderInfo;
  if (!businessSn) return;
  try {
    const res = await getMaterialPerUseNumber(businessSn);
    if (res.status === 200) {
      const {
        materialSkuIdPerUseNumberMap: perUseNumberMap, // 生产单-商品单品-物料单品: 单件用量
        goodSkuUseMaterialSku: { materialSkuIdGoodSkuIdMap: goodSkuIdMap }, // 生产单号-物料单品：使用到的商品单品
        produceGoodSkuNumber: goodSkuNumber, // 生产单-商品单品：下单数
      } = res.data;
      const preCutNumberDatas: any[] = []; // 存储预裁数相关数据
      Object.entries(goodSkuIdMap).forEach(([key, value]: any) => {
        const [proSn, materialSkuId] = key.split('-');
        const preCutNumberInfo: Recordable = {
          materialSkuId,
          goodsInfo: [],
        };
        const goodSkuTotal = value.reduce((pre: any, cur: any) => {
          const produceGoodSku = `${proSn}-${cur}`; // 生产单-商品单品
          const perUseNumberId = `${produceGoodSku}-${materialSkuId}`; // 生产单-商品单品-物料单品
          const perUseNumber = perUseNumberMap[perUseNumberId]; // 单件用量
          if (perUseNumber) {
            preCutNumberInfo.goodsInfo.push({
              perUseNumber,
              produceGoodSku: goodSkuNumber[produceGoodSku], // 下单数
            });
            return accAdd(pre, goodSkuNumber[produceGoodSku] || 0);
          }
          return pre;
        }, 0);
        preCutNumberInfo.goodSkuTotal = goodSkuTotal; // 总的下单数
        preCutNumberDatas.push(preCutNumberInfo);
      });
      list.forEach((item: any) => {
        const preCutNumberData = preCutNumberDatas.find(
          (preCutNumber: any) =>
            Number(preCutNumber.materialSkuId) === item.materialSkuId
        );
        if (preCutNumberData) {
          item.preCutNumberData = preCutNumberData;
          if (isSelectPro) {
            setPreCutNumber(item, preCutNumField, isSelectPro);
          }
        }
      });
    }
  } catch (error) {
    console.error(error);
  }
};

// 物料领料/发料设置预裁数
export const setPreCutNumber = (
  row: Recordable,
  field: string,
  isSelectPro = false
) => {
  const { preCutNumberData, number, ableIssueNumber } = row;
  if (isUndefined(preCutNumberData)) {
    row[field] = '-';
    return;
  }
  const num =
    isSelectPro && field === 'issuedPreCutNumber' ? ableIssueNumber : number;
  const { goodsInfo, goodSkuTotal } = preCutNumberData;
  const totalPreCutNumber = goodsInfo.reduce((prv: any, cur: any) => {
    const { perUseNumber, produceGoodSku } = cur;
    const skuPreCutNumber: any = Math.trunc(
      accDiv(
        accMul(num, produceGoodSku),
        accMul(goodSkuTotal, perUseNumber)
      ) as number
    );
    return accAdd(prv, skuPreCutNumber);
  }, 0);
  row[field] = totalPreCutNumber || 0;
};

// 处理发料预裁数、领料预裁数（按匹物料：计算匹的汇总行并同步更新匹行）
export const setSkuPreCutNumber = (
  sku: Recordable,
  list: Recordable[],
  preCutNumberField: string
) => {
  const { materialSkuId, isStockByCloth, isSkuTotalByMtView } = sku;
  const skuList = list.filter((item) => item.materialSkuId === materialSkuId);
  if (!isStockByCloth) {
    setPreCutNumber(sku, preCutNumberField);
    return;
  }
  let clothTotalRowTargetField = 0;
  if (isSkuTotalByMtView) {
    setPreCutNumber(sku, preCutNumberField);
    clothTotalRowTargetField = sku[preCutNumberField];
  } else {
    const clothTotalRow = skuList.find((item) => item.isSkuTotalByMtView);
    if (clothTotalRow) {
      setPreCutNumber(clothTotalRow, preCutNumberField);
      clothTotalRowTargetField = clothTotalRow[preCutNumberField];
    }
  }
  // 同步到匹行
  skuList.forEach((item) => {
    item[preCutNumberField] = clothTotalRowTargetField;
  });
};

export const getProduceOrdersSn = (
  row: Recordable,
  options: { batchField?: string; field?: string } = {}
) => {
  options.field = options.field || 'ordersSn';
  options.batchField = options.batchField || 'produceBatchSn';
  if (!hasProduceBatch.value) {
    return row[options.field];
  }
  return row[options.batchField] ? row[options.batchField] : row[options.field];
};

const IMAGE_REGEXP = /\.(jpeg|jpg|gif|png|svg|webp|jfif|bmp|dpg|avif)/i;
export const isImageUrl = (url: string): boolean => IMAGE_REGEXP.test(url);

// 根据对象key去重数组
export const uniqueObjArray = (
  arr: any
): Array<{ name: string; value: number }> => {
  return Array.from(
    arr
      .reduce((map: any, obj: any) => map.set(obj.value, obj), new Map())
      .values()
  );
};
function defaultComparator<T>(value: T, othVal: T) {
  return value === othVal;
}

export function arrayDeduplication<T>(arr: T[]): T[];
export function arrayDeduplication<T>(arr: T[], compareFn?: string): T[];
export function arrayDeduplication<T>(
  arr: T[],
  compareFn?: (value: T, othVal: T) => boolean
): T[];
export function arrayDeduplication<T>(...args: any[]): T[] {
  const list = args[0];
  let compareFn = args[1] ?? defaultComparator;

  if (typeof compareFn === 'string') {
    const key = compareFn as keyof T;
    compareFn = (value: T, othVal: T) => value[key] === othVal[key];
  }

  return list.reduce((acc: T[], cur: T) => {
    if (!acc.some((item: T) => compareFn(item, cur))) {
      acc.push(cur);
    }
    return acc;
  }, []);
}

export const getSysConfigValue = (key: string | string[]) => {
  const { sysConfigList } = storeToRefs(userStore);
  const tempKey = Array.isArray(key) ? key : [key];
  const resultArr: Recordable[] = [];
  tempKey.forEach((item: string) => {
    const configValue = computed(() => {
      const obj = getObject(sysConfigList.value, 'configKey', item);
      return obj ? Number(obj.configValue) : 0;
    });
    resultArr.push(configValue);
  });
  return resultArr;
};
// 获取判断当前url有没有携带指定参数，有则返回对应参数值
export const getQueryVariable = (variable: string) => {
  // 兼容加工厂本地调试获取参数
  const query = isLocal()
    ? window.location.hash.substring(3)
    : window.location.search.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
};

export const isFactory = () => {
  return !userStore.userInfo;
};

export function camelToSnake(camelCaseStr: string) {
  return camelCaseStr.replace(/[A-Z]/g, function (match) {
    return `_${match.toLowerCase()}`;
  });
}

function replacer(key: any, value: any) {
  if (value instanceof Map) {
    return {
      dataType: 'Map',
      value: Array.from(value.entries()), // or with spread: value: [...value]
    };
  }
  if (value instanceof Set) {
    return {
      dataType: 'Set',
      value: [...value], // or with spread: value: [...value]
    };
  }
  return value;
}

function reviver(key: any, value: any) {
  if (typeof value === 'object' && value !== null && value !== undefined) {
    if (value.dataType === 'Map') {
      return new Map(value.value);
    }
  }
  if (typeof value === 'object' && value !== null && value !== undefined) {
    if (value.dataType === 'Set') {
      return new Set(value.value);
    }
  }
  return value;
}

export function JSONStringify(params: any) {
  return JSON.stringify(params, replacer);
}

export function JSONParse(str: any) {
  return JSON.parse(str, reviver);
}

// 获取查询列表需要的列表字段
export const getListNeedField = (
  includeMap: Record<string, string>,
  needFieldMap: Record<string, boolean>
) => {
  const include: string[] = [];
  const isNeedCustomField = Object.keys(needFieldMap).some((field: string) =>
    field.includes(globalContants.CUSTOM_FIELD_PREFIX)
  );
  if (isNeedCustomField) {
    include.push('businessAttributeValues');
  }
  return Object.keys(includeMap).reduce((acc: string[], key: string) => {
    if (needFieldMap[key] && !acc.includes(includeMap[key])) {
      acc.push(includeMap[key] as string);
    }
    return acc;
  }, include);
};
interface ATTACHMENT_TIPS {
  accept?: string;
  maxSize?: number;
  maxSizeTipType?: string;
  limit?: number | string;
}
// 附件提示
export const getAttachmentTips = ({
  accept = '*',
  maxSize = globalContants.ATTACHEMENT_MAX_SIZE,
  maxSizeTipType = 'MB',
  limit = globalContants.ATTACHEMENT_MAX_COUNT,
}: ATTACHMENT_TIPS) => {
  const supportTypesText = (acceptType: string) => {
    const acceptValue = acceptType.toLowerCase();

    const typeMap: Record<string, string> = {
      'image/*': '图片',
      'video/*': '视频',
      'audio/*': '音频',
      '.docx': 'Word文档',
      '.xlsx': 'Excel表格',
      '*': '图片、视频、文件',
    };

    // 处理多类型情况
    if (acceptValue.includes(',')) {
      return acceptValue
        .split(',')
        .map(
          (type) =>
            typeMap[type.trim()] ||
            type.replace(/\*/g, '').replace(/\./g, '').replace(/\/\w+/g, '')
        )
        .join('、');
    }

    // 直接匹配已知类型 正则作用：避免undefined出现
    return (
      typeMap[acceptValue] ||
      acceptValue.replace(/\*/g, '').replace(/\./g, '').replace(/\/\w+/g, '')
    );
  };
  return `1、支持${supportTypesText(accept)}
          2、单个文件不超过${
            maxSize / 1024 / 1024
          }${maxSizeTipType}，最多${limit}个文件
           3、可以拖拽文件到上传框`;
};

export const clearQueryCache = (key: string | string[]) => {
  const tempKey = Array.isArray(key) ? key : [key];
  tempKey.forEach((key: string) => {
    localStorage.removeItem(key);
  });
};
type DeadlineFields = {
  compareField?: string; // 完成时间字段名
  deadlineField?: string; // 截止时间字段名
  displayFinishInfo?: boolean; // 是否展示完成信息
};
type FormatterOptions = {
  fields?: DeadlineFields; // 字段配置
  showDate?: boolean; // 是否展示日期
  isFinishedChecker?: () => boolean; // 完成状态判断函数
};
type SourceData = { row: Recordable; [key: string]: Recordable };
export const formatterDeadlineTime = (
  { row }: SourceData,
  options: FormatterOptions = {}
): string => {
  const {
    fields: {
      compareField = 'finishTime',
      deadlineField = 'deadlineTime',
      displayFinishInfo = true,
    } = {},
    showDate = true,
    isFinishedChecker = () => false,
  } = options;
  const deadlineTime = row[deadlineField];
  if (!deadlineTime) return '-';
  const deadline = getBeginningTime(deadlineTime);
  const today = getBeginningTime(new Date());
  const formatterDeadline = formatTime(deadlineTime, 'YYYY-MM-DD');
  // 已完成展示什么时候完成或者逾期多少天完成
  if (isFinishedChecker()) {
    if (displayFinishInfo) {
      const compareTime = row[compareField];
      const finish = getBeginningTime(compareTime);
      const formatterFinish = formatTime(compareTime, 'YYYY-MM-DD');
      const diff = getDiffDays(deadline, finish);
      if (diff <= 0) {
        return `${formatterFinish}完成`;
      }
      return `逾期${diff}天(${formatterFinish}完成)`;
    }
    return '-';
  }
  // 未完成展示逾期或者剩下多少天或者小时
  const diff = getDiffDays(deadline, today);
  if (diff > 0) {
    const msg = `逾期${diff}天`;
    return showDate ? `${msg}(${formatterDeadline})` : msg;
  }
  if (diff < 0) {
    const msg = `剩${Math.abs(diff)}天`;
    return showDate ? `${msg}(${formatterDeadline})` : msg;
  }
  const diffHour =
    accDiv(
      accDiv(accDiv(Math.abs(+new Date() - deadlineTime), 1000), 60),
      60
    ) || 0;
  const diffMinute =
    accMul(accSub(diffHour.toFixed(2), Math.floor(diffHour)), 60) || 0;
  const msg = `剩${Math.floor(diffHour)}小时${Math.floor(diffMinute)}分钟`;
  return showDate
    ? `${msg}(${formatTime(deadlineTime, 'YYYY-MM-DD HH:mm')})`
    : msg;
};
export const formatterDeadlineTimeClass = (
  { row }: SourceData,
  options: FormatterOptions = {}
): string => {
  const {
    fields: {
      compareField = 'finishTime',
      deadlineField = 'deadlineTime',
      displayFinishInfo = true,
    } = {},
    isFinishedChecker = () => false,
  } = options;
  const deadlineTime = row[deadlineField];
  if (!deadlineTime) return '';
  const deadline = getBeginningTime(deadlineTime);
  const today = getBeginningTime(new Date());
  // 待完成
  const handleTodo = () => {
    if (deadline < today) {
      return 'color-red';
    }
    if (deadline > today) {
      return 'color-orange';
    }
    return '';
  };
  // 已完成
  const handleFinish = () => {
    if (isFinishedChecker()) {
      const compareTime = row[compareField];
      if (displayFinishInfo && compareTime > deadlineTime) {
        return 'color-red';
      }
      return '';
    }
    return handleTodo();
  };

  return handleFinish();
};

// 计算剩余天数
export const getRemainingDaysByTime = (
  time: string | number | Date
): number => {
  const nowDayjs = dayjs(new Date()).format('YYYY-MM-DD');
  const ret = dayjs(dayjs(time).format('YYYY-MM-DD')).diff(
    dayjs(nowDayjs),
    'day'
  );
  return ret >= 0 ? ret + 1 : ret - 1;
};

// 设置用户自定义配置
export const setUserConfigList: any = async (data: any) => {
  try {
    const res = await saveUserConfig({ userConfigList: [data] });
    if (res.status === 200) {
      await getUserConfig();
    }
    return res;
  } catch (error: any) {
    return Promise.reject(error);
  }
};
// 获取用户自定义配置
export const getUserConfig: any = async () => {
  try {
    const res = await queryUserConfigList({});
    if (res.data) {
      userStore.setInfo({
        userConfigList: res.data,
      });
    }
    return res;
  } catch (error: any) {
    return Promise.reject(error);
  }
};

/**
 * @desc 获取流程配置值
 * @param key 流程配置key
 * @returns 流程配置值
 */
export const getFlowConfigValue = (
  key: FLOW_BUSINESS_KEY | FLOW_BUSINESS_KEY[]
) => {
  const { flowConfigList } = storeToRefs(userStore);
  const tempKey = Array.isArray(key) ? key : [key];
  const resultArr: Recordable[] = [];
  tempKey.forEach((item: string) => {
    const enable = computed(() => {
      const obj = getObject(flowConfigList.value, 'code', item);
      return obj ? Number(obj.enable) : 0;
    });
    resultArr.push(enable);
  });
  return resultArr;
};
// 去除小数点后多余的零
export const removeTrailingZeros = (num: number | undefined) => {
  if (!num && num !== 0) return num;
  const str = num.toString();
  // 如果没有小数点，直接返回原数字
  if (str.indexOf('.') === -1) {
    return num;
  }
  // 去除末尾的零
  let trimmed = str.replace(/0+$/, '');
  // 如果小数点后没有数字了，去掉小数点
  if (trimmed.endsWith('.')) {
    trimmed = trimmed.slice(0, -1);
  }
  return parseFloat(trimmed);
};

// 获取阶梯损耗率范围
export const getLossRateRange = (lossRateStairs: Recordable[]) => {
  if (!lossRateStairs?.length) return '';
  const lossRateList = uniq(lossRateStairs.map((item) => item.lossRate)).sort(
    (a, b) => a - b
  );
  const [min] = lossRateList;
  if (lossRateList.length === 1) return `${min}%`;
  return `${min}%~${lossRateList[lossRateList.length - 1]}%`;
};

/**
 * 根据下单数算出对应范围的损耗率
 * @param item 一条bom明细
 * @param lossRate 单品损耗率
 * @param numberSum 全部成衣下单数总数
 * @param number 当前物料涉及使用的成衣下单数总数
 * @returns 该物料单品对应下单数范围的损耗率
 */
export const getLossRateByOrderNumber = (
  item: Recordable,
  lossRate: number = 0,
  numberSum: number = 0,
  number: number = 0
) => {
  const {
    lossRateStairEnable, // 阶梯损耗率开关 0 | 1
    lossRateStairType, // 阶梯损耗率-下单数范围 2-全部成衣 3-按当前物料涉及使用的成衣
    lossRateStairItems = [],
  } = item;
  if (!lossRateStairEnable) return lossRate;
  const orderNumber = lossRateStairType === 3 ? number : numberSum;
  return (
    cloneDeep(lossRateStairItems)
      .sort((a: Recordable, b: Recordable) => b.orderNumber - a.orderNumber) // 倒序
      .find((e: Recordable) => orderNumber >= e.orderNumber)?.lossRate ?? 0
  );
};

// 获取单号英文大写字母前缀
export const getOrderSnPrefix = (orderSn: string = '') =>
  orderSn?.match(/^[A-Z]+/)?.[0] ?? '';

// 格式化年份
export const formatterYear = (
  year: any,
  yearRanges: any,
  placeHolder: string = ''
) => {
  if (!year) return placeHolder;
  return `${year}${yearRanges === 1 ? '上半年' : '下半年'}`;
};

export const getRestDaysList = () => {
  const list = sysConfigMap.value?.workdayTimeSettingConfig
    ? restDaysList.value
    : defaultRestDays;
  return cloneDeep(list);
};
export const autoConvertKnitted = (row: Recordable) => {
  const { fabricType, unitName, purchaseUnitName } = row || {};
  return (
    fabricType === 1 &&
    unitName === '米' &&
    (purchaseUnitName ?? '').toLowerCase() === 'kg'
  );
};

// 根据物料面料类型，判断相关字段是否可以编辑
export const checkTypeFun = (
  column: Recordable,
  fabricType: number,
  row: Recordable
) => {
  const { field, editType, typeFun } = column;
  if (
    ['gramWeight', 'materialGramWeight'].includes(field) &&
    ![1, 2].includes(fabricType)
  )
    return false;
  if (autoConvertKnitted(row) && field === 'convertRate') return false;
  if (editType !== 'number' || !isFunction(typeFun)) return true;
  return typeFun(fabricType, field);
};

export const isValidNumber = (value: any) => {
  return isNumber(value) && !Number.isNaN(value);
};

// 根据ids中id的索引来排序list中的item.id
export const sortByIds = (list: any[], ids: any[]) => {
  const idToIndexMap = ids.reduce(
    (acc: Recordable, id: number, index: number) => {
      acc[id] = index;
      return acc;
    },
    {}
  );
  list.sort(
    (a: Recordable, b: Recordable) => idToIndexMap[a.id] - idToIndexMap[b.id]
  );
  return list;
};
export const generateNumericNanoId = (prefix: string = '') => {
  return prefix + customAlphabet('1234567890')();
};

export function toDataUrl(url: string) {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve('');
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
}

export const fetchDataSerially = async (
  reqFn: (...res: any[]) => Promise<any>,
  data: Recordable,
  params?: Recordable,
  options: Recordable = {}
) => {
  const { callMode = 'data-params' } = options;
  const paramsVal = params ?? {};

  // 根据调用方式构建请求参数
  const buildRequestArgs = (pageParams = {}): any[] => {
    switch (callMode) {
      case 'data-params':
        return [data, pageParams];
      case 'merge':
        return [{ ...pageParams, ...data }];
      case 'params-only':
        return [pageParams];
      case 'params-data':
      default:
        return [pageParams, data];
    }
  };
  const results: any[] = [];
  try {
    const res = await reqFn(...buildRequestArgs(paramsVal));
    const { pageCount = 1 } = res.data?.pager || {};

    if (pageCount <= 1) {
      return res.data?.list || [];
    }

    results.push(...(res.data?.list || []));

    // 并发请求剩余页面
    const requestQueue: Promise<Recordable>[] = [];
    for (let i = 1; i < pageCount; i += 1) {
      const pageParams = { ...paramsVal, page: i + 1 };
      requestQueue.push(reqFn(...buildRequestArgs(pageParams)));
    }

    const resList = await Promise.all(requestQueue);
    resList.forEach((resData: Recordable) => {
      if (resData?.status === 200 && resData.data) {
        results.push(...(resData.data.list || []));
      }
    });
  } catch (error) {
    console.error(error);
    return [];
  }

  return results;
};

export const checkUpdateBomFinish = (bomFinish: number) => {
  if (
    checkAuthority('goodsDataMaterialBaseEdit') &&
    [0, 1].includes(bomFinish)
  ) {
    return true;
  }
  return false;
};

export const goToEditGoods = async (router: any, query: any) => {
  const { id: goodsId, ...restQuery } = query;
  if (!goodsId) return;
  if (isAllowGoodsEdit.value) {
    router.push({
      name: 'EditGoods',
      query: { id: goodsId, ...restQuery, jump: 'true' },
    });
    return;
  }
  try {
    const res = await safeGetGoodsOrDesign({ id: goodsId });
    if (res.status === 200 && res.data?.design) {
      const { design = {} } = res.data.design;
      if (design?.id) {
        router.push({
          name: 'EditDesignPro',
          query: { id: design?.id },
        });
      }
    }
  } catch (error) {
    // router.push({
    //   name: 'AddGoods',
    // });
    console.error(error);
  }
};
